import {
  cn,
  IconButtonAsButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';

import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { IconFullscreen, IconMinimize, IconMinimizeV2 } from '../icons';

interface ChatHeaderActionsProps {
  className?: string;
}

/**
 * Header actions component for fullscreen toggle and close button.
 * Uses context to access state and handlers.
 */
export function ChatHeaderActions({ className }: ChatHeaderActionsProps) {
  const { isStandaloneMode, isFullscreen, setIsFullscreen } = useChatOverlayContext();
  const fullscreenToggleLabel = isFullscreen ? 'Minimize' : 'Maximize';

  if (isStandaloneMode) return null;

  return (
    <TooltipProvider>
      <div className={cn('flex items-center gap-2', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButtonAsButton
              aria-label={fullscreenToggleLabel}
              data-testid="chat-fullscreen-toggle-button"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? <IconMinimizeV2 /> : <IconFullscreen />}
            </IconButtonAsButton>
          </TooltipTrigger>
          <TooltipContent>{fullscreenToggleLabel}</TooltipContent>
        </Tooltip>
        <ChatMinimizeButton />
      </div>
    </TooltipProvider>
  );
}

export function ChatMinimizeButton() {
  const { isStandaloneMode } = useChatOverlayContext();

  if (isStandaloneMode) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <ChatMinimizeIconButton />
        </TooltipTrigger>
        <TooltipContent>Minimize</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ChatMinimizeIconButton() {
  const { onClose } = useChatOverlayContext();

  return (
    <IconButtonAsButton aria-label="Minimize chat" data-testid="chat-minimize-button" onClick={onClose}>
      <IconMinimize />
    </IconButtonAsButton>
  );
}
